import { createReducer, on } from '@ngrx/store';
import { markCached } from '@ps/utils';

import { INITIAL_NOTIFICATION_STATE } from './notification.constants';
import { NotificationState } from './notification.model';
import { NotificationApiActions } from './notification.actions';

export const notificationReducer = createReducer(
  INITIAL_NOTIFICATION_STATE,

  on(
    NotificationApiActions.loadNotificationsStarted,
    (state): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      loading: true,
      error: null,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
    }),
  ),

  on(
    NotificationApiActions.loadNotificationsSuccess,
    (state, { notifications, cachedAt }): NotificationState => ({
      notifications,
      unseenCount: state.unseenCount,
      loading: false,
      error: null,
      notificationsCachedAt: cachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
    }),
  ),

  on(
    NotificationApiActions.loadNotificationsFailure,
    (state, { error }): NotificationState => ({
      notifications: [],
      unseenCount: state.unseenCount,
      loading: false,
      error,
      notificationsCachedAt: null,
      unseenCountCachedAt: state.unseenCountCachedAt,
    }),
  ),

  on(
    NotificationApiActions.loadUnseenCountSuccess,
    (state, { unseenCount, cachedAt }): NotificationState => ({
      notifications: state.notifications,
      unseenCount,
      loading: state.loading,
      error: null,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: cachedAt,
    }),
  ),

  on(
    NotificationApiActions.loadUnseenCountFailure,
    (state, { error }): NotificationState => ({
      notifications: state.notifications,
      unseenCount: 0,
      loading: state.loading,
      error,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: null,
    }),
  ),

  on(
    NotificationApiActions.markAllViewedSuccess,
    (state): NotificationState => ({
      notifications: state.notifications.map((notification) => ({
        ...notification,
        viewed: true,
      })),
      unseenCount: 0,
      loading: state.loading,
      error: null,
      notificationsCachedAt: markCached(),
      unseenCountCachedAt: markCached(),
    }),
  ),

  on(
    NotificationApiActions.markViewedSuccess,
    (state, { notification }): NotificationState => {
      const previous = state.notifications.find(
        (existingNotification) =>
          existingNotification.notificationId === notification.notificationId,
      );
      // Computed locally rather than left for the follow-up refetch: that
      // refetch is now behind a 60s cache gate (see notification.effects.ts)
      // and can be silently skipped, leaving the badge stale.
      const unseenDelta = !previous
        ? 0
        : Number(previous.viewed) - Number(notification.viewed);

      return {
        notifications: state.notifications.map((existingNotification) =>
          existingNotification.notificationId === notification.notificationId
            ? notification
            : existingNotification,
        ),
        unseenCount: Math.max(0, state.unseenCount + unseenDelta),
        loading: state.loading,
        error: null,
        notificationsCachedAt: markCached(),
        unseenCountCachedAt: markCached(),
      };
    },
  ),

  on(
    NotificationApiActions.markAllViewedFailure,
    NotificationApiActions.markViewedFailure,
    (state, { error }): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      loading: state.loading,
      error,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
    }),
  ),
);
